import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Decode, reducedMotion, useClock } from '../lib/fx';
import { subscribeN4 } from '../lib/n4';
import { NotificationCenter } from '../components/NotificationCenter';

// A · Élő vezérlő héj: bal sáv (menü csúszkával, élő ügynök-pulzus, gép-sáv), felső sáv (dekódoló cím, hívás-futószalag, óra, harang).
const NAV: { label: string; short: string; to: string; icon: string }[] = [
  { label: 'Dashboard', short: 'Áttekintés', to: '/', icon: '◆' },
  { label: 'Terminál', short: 'Terminál', to: '/terminal', icon: '›_' },
  { label: 'Projektek', short: 'Projektek', to: '/projects', icon: '▦' },
  { label: 'Ügynök-chat', short: 'Chat', to: '/chat', icon: '◉' },
  { label: 'Teljesítmény', short: 'Gép', to: '/performance', icon: '∿' },
  { label: 'Weboldalak', short: 'Web', to: '/sites', icon: '◎' },
  { label: 'Beállítások', short: 'Beállít.', to: '/settings', icon: '⚙' },
];
interface Session { id: string; name: string; projectId: number | null; alive: boolean; kind?: 'main' | 'worker'; lastOutputAt?: number }
interface Call { project_id: number; project: string | null; tool: string; label: string; detail: string; at: number }
interface Brief { cpu: number | null; mem: number }

// Egy ügynök pulzusvonala: alapvonal enyhe zajjal; ha a projektje APM-et hív, EKG-szerű kilengés, alatta a hívás neve felvillan.
const SHAPE = [15, 12, 18, 2, 28, 9, 15];
function AgentPulse({ s, working, onOpen }: { s: Session; working: boolean; onOpen: () => void }) {
  const pathRef = useRef<SVGPathElement>(null);
  const [label, setLabel] = useState<{ text: string; flash: boolean }>({ text: working ? 'dolgozik' : 'várakozik', flash: false });
  const spike = useRef(0); const workingRef = useRef(working); workingRef.current = working;
  useEffect(() => { setLabel((l) => (l.flash ? l : { text: working ? 'dolgozik' : 'várakozik', flash: false })); }, [working]);
  useEffect(() => {
    const onCall = (e: Event) => { const c = (e as CustomEvent<Call>).detail; if (c.project_id !== s.projectId) return; spike.current = SHAPE.length; setLabel({ text: c.label, flash: true }); window.setTimeout(() => setLabel((l) => ({ ...l, flash: false })), 900); };
    window.addEventListener('apm-call', onCall);
    return () => window.removeEventListener('apm-call', onCall);
  }, [s.projectId]);
  useEffect(() => {
    const pts = Array<number>(60).fill(15);
    const draw = () => { pathRef.current?.setAttribute('d', pts.map((y, x) => `${x ? 'L' : 'M'}${(x / 59) * 200} ${y.toFixed(1)}`).join(' ')); };
    if (reducedMotion()) { draw(); return undefined; }
    const t = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      let v = 15 + (workingRef.current ? (Math.random() - 0.5) * 2.2 : 0);
      if (spike.current > 0) { v = SHAPE[SHAPE.length - spike.current] ?? 15; spike.current--; }
      pts.push(v); pts.shift(); draw();
    }, 70);
    return () => clearInterval(t);
  }, []);
  return (
    <a className={`a-agent ${working ? '' : 'idle'}`} onClick={onOpen} title={`${s.name} — megnyitás`}>
      <div className="an"><i className={`a-lamp ${working ? 'on' : 'idle'}`} /><span className="nm">{s.name}</span></div>
      <svg viewBox="0 0 200 30" preserveAspectRatio="none" aria-hidden="true"><path ref={pathRef} d="M0 15 L200 15" /></svg>
      <div className={`ac ${label.flash ? 'flash' : ''}`}>{label.text}</div>
    </a>
  );
}

export function Shell() {
  const { logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const clock = useClock();
  const [sessions, setSessions] = useState<Session[]>([]);
  // Ügynök-chat: olvasatlan ügynök-üzenetek száma a menüben (a chat-oldal nullázza)
  const [chatUnread, setChatUnread] = useState(0);
  const [projects, setProjects] = useState<{ id: number; name: string }[]>([]);
  const [calls, setCalls] = useState<Call[]>([]);
  const [brief, setBrief] = useState<Brief | null>(null);
  const [limit, setLimit] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const navRef = useRef<HTMLElement>(null); const inkRef = useRef<HTMLSpanElement>(null);
  // APM újraindítása: megerősítés (ki dolgozik most), majd várakozás, amíg a szerver visszajön, és újratöltés
  const [restart, setRestart] = useState<null | 'ask' | 'going'>(null);
  const [restartInfo, setRestartInfo] = useState<{ name: string; kind: string; working: boolean }[]>([]);
  const [restartErr, setRestartErr] = useState('');
  const [restartRel, setRestartRel] = useState<{ version: string | null; need: 'server' | 'web' | 'none'; note: string } | null>(null);
  const askRestart = async () => { setRestartErr(''); try { const r = await api<{ sessions: { name: string; kind: string; working: boolean }[]; release?: { version: string | null; need: 'server' | 'web' | 'none'; note: string } }>('/api/n4/restart'); setRestartInfo(r.sessions); setRestartRel(r.release ?? null); } catch { setRestartInfo([]); setRestartRel(null); } setRestart('ask'); };
  const doRestart = async () => {
    try { await api('/api/n4/restart', { method: 'POST' }); } catch (e) { setRestartErr(e instanceof Error ? e.message : 'Nem sikerült.'); return; }
    setRestart('going'); const t0 = Date.now(); let down = false;
    const poll = async () => { try { const h = await fetch('/healthz', { cache: 'no-store' }); if (h.ok && (down || Date.now() - t0 > 12_000)) { window.location.reload(); return; } if (!h.ok) down = true; } catch { down = true; } if (Date.now() - t0 < 120_000) window.setTimeout(() => void poll(), 1200); };
    window.setTimeout(() => void poll(), 2500);
  };

  // Élő ablakok (5 mp), projektnevek, hívás-előzmény + élő hívások, gép-sáv (4 mp), keret (2 perc)
  useEffect(() => {
    const load = () => { if (document.visibilityState === 'visible') void api<{ sessions: Session[] }>('/api/sessions').then((r) => { setSessions(r.sessions); setNow(Date.now()); }).catch(() => {}); };
    load(); const t = window.setInterval(load, 5000); return () => clearInterval(t);
  }, []);
  useEffect(() => { void api<{ projects: { id: number; name: string }[] }>('/api/projects').then((r) => setProjects(r.projects)).catch(() => {}); }, [location.pathname]);
  useEffect(() => { void api<{ calls: Call[] }>('/api/n4/calls').then((r) => setCalls(r.calls)).catch(() => {}); }, []);
  useEffect(() => subscribeN4((ev) => {
    if (ev.type !== 'agentcall') return;
    const c = ev as unknown as Call;
    setCalls((p) => [c, ...p].slice(0, 40));
    window.dispatchEvent(new CustomEvent('apm-call', { detail: c }));
  }), []);
  useEffect(() => {
    const load = () => { if (document.visibilityState === 'visible') void api<Brief>('/api/n4/perf/brief').then(setBrief).catch(() => {}); };
    load(); const t = window.setInterval(load, 4000); return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const load = () => void api<{ session?: { pct: number } }>('/api/usage').then((u) => setLimit(u.session?.pct ?? null)).catch(() => {});
    load(); const t = window.setInterval(load, 120_000); return () => clearInterval(t);
  }, []);

  // Menü-csúszka a kijelölt pont alá (betűtípus betöltése és átméretezés után is)
  useLayoutEffect(() => {
    const place = () => {
      const nav = navRef.current; const ink = inkRef.current; if (!nav || !ink) return;
      const on = nav.querySelector<HTMLAnchorElement>('a.active'); if (!on) { ink.style.opacity = '0'; return; }
      ink.style.opacity = '1';
      const horizontal = window.innerWidth <= 640;
      ink.style.transform = horizontal ? `translateX(${on.offsetLeft}px)` : `translateY(${on.offsetTop}px)`;
      ink.style.width = horizontal ? `${on.offsetWidth}px` : '';
      ink.style.height = horizontal ? '100%' : `${on.offsetHeight}px`;
    };
    place(); void document.fonts?.ready.then(place);
    window.addEventListener('resize', place); return () => window.removeEventListener('resize', place);
  }, [location.pathname]);

  const live = sessions.filter((s) => s.alive && s.kind !== 'worker' && s.projectId != null);
  const isWorking = (s: Session) => now - (s.lastOutputAt ?? 0) < 6000;
  const path = location.pathname;
  useEffect(() => { const load = () => void api<{ unread: number }>('/api/n4/chat?limit=1').then((r) => setChatUnread(r.unread)).catch(() => {}); load(); const t = window.setInterval(load, 20_000); return () => window.clearInterval(t); }, [path]);
  useEffect(() => subscribeN4((ev) => { if (ev.type === 'chat' && (ev as { from_project_id?: number | null }).from_project_id != null && !location.pathname.startsWith('/chat')) setChatUnread((n) => n + 1); }), [location.pathname]);
  useEffect(() => { const z = () => setChatUnread(0); window.addEventListener('apm-chat-seen', z); return () => window.removeEventListener('apm-chat-seen', z); }, []);
  const projMatch = path.match(/^\/projects\/(\d+)/);
  const projName = projMatch ? projects.find((p) => p.id === Number(projMatch[1]))?.name ?? '' : '';
  const crumb = path === '/' ? 'Dashboard' : path.startsWith('/terminal') ? 'Terminál' : projMatch ? `Projektek / ${projName}` : path.startsWith('/projects') ? 'Projektek' : path.startsWith('/performance') ? 'Teljesítmény' : path.startsWith('/sites') ? 'Weboldalak' : path.startsWith('/chat') ? 'Ügynök-chat' : path.startsWith('/settings') ? 'Beállítások' : '';
  const ago = (t: number) => { const m = Math.round((Date.now() - t) / 60000); return m < 1 ? 'most' : `${m} p`; };
  const tickItems = calls.slice(0, 16);

  return (
    <div className="a-shell">
      <div className="a-bg" aria-hidden="true" />
      <aside className="a-rail">
        <NavLink to="/" className="a-brand"><span className="a-tiles" aria-hidden="true"><i /><i /><i /><i /></span><span className="bt">APM</span></NavLink>
        <nav className="a-nav" ref={navRef}>
          <span className="a-ink" ref={inkRef} aria-hidden="true" />
          {NAV.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === '/'} title={item.label}>
              <span className="ic" aria-hidden="true">{item.icon}</span><span className="lb">{item.label}</span><span className="sh">{item.short}</span>{item.to === '/chat' && chatUnread > 0 && <span className="nb">{chatUnread}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="a-live">
          <h4><i className={`a-lamp ${live.some(isWorking) ? 'on' : live.length ? 'idle' : ''}`} /><span className="nm">Élő ügynökök</span></h4>
          {live.length === 0 && <div className="a-noagent">Most nem fut ügynök-ablak.</div>}
          {live.map((s) => <AgentPulse key={s.id} s={s} working={isWorking(s)} onOpen={() => navigate(`/projects/${s.projectId}`)} />)}
        </div>
        <div className="a-machine">
          <NavLink to="/performance" title="Teljesítmény"><span>CPU</span><span className="t"><i style={{ width: `${brief?.cpu ?? 0}%` }} /></span><span>{brief?.cpu != null ? `${Math.round(brief.cpu)}%` : '—'}</span></NavLink>
          <NavLink to="/performance" title="Teljesítmény"><span>RAM</span><span className="t"><i style={{ width: `${brief?.mem ?? 0}%` }} /></span><span>{brief ? `${Math.round(brief.mem)}%` : '—'}</span></NavLink>
          <NavLink to="/" title="Munkamenet-limit"><span>LIMIT</span><span className="t"><i style={{ width: `${limit ?? 0}%` }} /></span><span>{limit != null ? `${limit}%` : '—'}</span></NavLink>
        </div>
      </aside>
      <main className="a-main">
        <div className="a-top">
          <span className="a-crumb"><Decode text={crumb} /></span>
          <div className="a-ticker" title="Az ügynökök legutóbbi APM-hívásai">
            {tickItems.length > 0 && <div className="tk">{[0, 1].map((k) => tickItems.map((c, i) => <span key={`${k}-${i}`}><b>{c.project ?? `#${c.project_id}`}</b>{c.label}{c.detail ? ` · ${c.detail}` : ''} <span style={{ color: '#4d505c' }}>{ago(c.at)}</span></span>))}</div>}
          </div>
          <span className="a-clock">{clock}</span>
          <NotificationCenter />
          <button className="a-logout a-restart" onClick={() => void askRestart()} title="Az APM szerver újraindítása (minden ügynök-ablak bezárul, a beszélgetések megmaradnak)">APM Restart</button>
          <button className="a-logout" onClick={() => void logout()} title="Kilépés">Kilépés</button>
        </div>
        <div className="a-agents-m">{live.map((s) => <a key={s.id} onClick={() => navigate(`/projects/${s.projectId}`)}><i className={`a-lamp ${isWorking(s) ? 'on' : 'idle'}`} />{s.name}</a>)}</div>
        {restart && (
          <div className="a-overlay fixed inset-0 z-[60] flex items-center justify-center p-4" onClick={() => restart === 'ask' && setRestart(null)}>
            <div className="a-panel a-pad a-modal-in w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              {restart === 'ask' ? (<>
                <h3 className="mb-2 text-[20px] text-neutral-100">APM újraindítása</h3>
                <p className="a-sub" style={{ marginTop: 0 }}>Minden ügynök-ablak bezárul; a beszélgetések megmaradnak, megnyitáskor folytatódnak. Az oldal magától újratölt.</p>
                {restartRel && <p className={restartRel.need === 'server' ? 'a-bp-warn' : 'a-sub'} style={{ marginTop: 8 }}>{restartRel.need === 'server' ? '⟳ ' : restartRel.need === 'web' ? '↻ ' : '✓ '}{restartRel.note}{restartRel.version ? ` (kiadás ${restartRel.version})` : ''}{restartRel.need === 'web' ? <> <button className="a-btn sm pri" style={{ marginLeft: 8 }} onClick={() => window.location.reload()}>Újratöltés</button></> : null}</p>}
                {restartInfo.length > 0 && <ul className="a-rs-list">{restartInfo.map((x, i) => <li key={i}><i className={`a-lamp ${x.working ? 'on' : 'idle'}`} />{x.name}{x.kind === 'worker' ? ' (munkás)' : ''}<span>{x.working ? 'dolgozik most' : 'tétlen'}</span></li>)}</ul>}
                {restartInfo.some((x) => x.working) && <p className="a-bp-warn" style={{ marginTop: 8 }}>Van, aki épp dolgozik: az ő munkája megszakad.</p>}
                {restartErr && <p className="a-login-err">{restartErr}</p>}
                <div className="mt-4 flex gap-2"><button className="a-btn solid" onClick={() => void doRestart()}>Újraindítás</button><button className="a-btn ghost" onClick={() => setRestart(null)}>Mégse</button></div>
              </>) : (<><h3 className="mb-2 text-[20px] text-neutral-100">Újraindul…</h3><p className="a-sub" style={{ marginTop: 0 }}>Amint a szerver visszajön, az oldal magától újratölt.</p><div className="a-meter" style={{ marginTop: 12 }}><i className="a-runbar-i" /></div></>)}
            </div>
          </div>
        )}
        <div className="a-content">
          <div className="a-scan" key={`scan-${path}`} aria-hidden="true" />
          <div className="a-page" key={path}><Outlet /></div>
        </div>
      </main>
    </div>
  );
}
